<script setup>
// core
import { useVuelidate } from '@vuelidate/core'
import { minLength, required, sameAs } from '@vuelidate/validators'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n';

// components
import InputField from "@/components/Recurring/InputField.vue"
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue"

// ---

const props = defineProps({
    loading: {
        type: Boolean,
        required: true,
        default: false
    }
})

const emit = defineEmits(["submit"])

// ---
const { t: $t } = useI18n();
const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmationPassword = ref(false);

const formData = ref({
    current_password: null,
    password: null,
    password_confirmation: null
})

const rules = {
    current_password: { minLength: minLength(6), required },
    password: {
        minLength: minLength(6),
        required,
        differentFromCurrent: (value) => value !== formData.value.current_password
    },
    password_confirmation: {
        minLength: minLength(6),
        required,
        sameAsPassword: (value) => value === formData.value.password
    }
}

const v$ = useVuelidate(rules, formData, {
    $lazy: true
})

const getErrorMessage = (field) => {
    if (!v$.value || !v$.value[field] || !v$.value[field].$error) return '';

    if (v$.value[field].required.$invalid) {
        return $t('validation.required');
    }

    if (v$.value[field].minLength.$invalid) {
        return $t('validation.min', { length: 6 });
    }

    if (field === 'password' && v$.value[field].differentFromCurrent.$invalid) {
        return $t('validation.password_different');
    }

    if (field === 'password_confirmation' && v$.value[field].sameAsPassword.$invalid) {
        return $t('validation.password_mismatch');
    }

    return '';
}

const handleSubmit = async () => {
    const isFormCorrect = await v$.value.$validate()

    if (isFormCorrect) {
        emit('submit', formData.value)
    }
}

</script>

<template>
    <form class="update-password" @submit.prevent="handleSubmit">
        <h3 class="update-password__title">
            {{ $t('profile.update_password.title') }}
        </h3>
        <div class="update-password__fields">
            <div class="update-password__item">
                <label class="update-password__item-label" for="old_password">{{ $t('profile.update_password.current_password') }}</label>
                <div class="update-password__item-input">
                    <InputField v-model="formData.current_password" :is-focus-in-start="false"
                        :type="showCurrentPassword ? 'text' : 'password'" autocomplete="current-password" name="old_password" id="old_password">
                        <button class="input-password-icon" @click.prevent="showCurrentPassword = !showCurrentPassword">
                            <i :class="!showCurrentPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                        </button>
                    </InputField>
                </div>

                <span class="update-password__item-error">
                    {{ getErrorMessage('current_password') }}
                </span>
            </div>

            <div class="update-password__item">
                <label class="update-password__item-label" for="new_password">{{ $t('profile.update_password.new_password') }}</label>
                <div class="update-password__item-input">
                    <InputField v-model="formData.password" :is-focus-in-start="false"
                        :type="showNewPassword ? 'text' : 'password'" autocomplete="new-password" name="new_password" id="new_password">
                        <button class="input-password-icon" @click.prevent="showNewPassword = !showNewPassword">
                            <i :class="!showNewPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                        </button>
                    </InputField>
                </div>
                <span class="update-password__item-error">
                    {{ getErrorMessage('password') }}
                </span>
            </div>

            <div class="update-password__item">
                <label class="update-password__item-label" for="confirm">{{ $t('profile.update_password.confirmation_password') }}</label>
                <div class="update-password__item-input">
                    <InputField v-model="formData.password_confirmation" :is-focus-in-start="false"
                        :type="showConfirmationPassword ? 'text' : 'password'" autocomplete="new-password" name="confirm" id="confirm">
                        <button class="input-password-icon"
                            @click.prevent="showConfirmationPassword = !showConfirmationPassword">
                            <i :class="!showConfirmationPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                        </button>
                    </InputField>
                </div>
                <span class="update-password__item-error">
                    {{ getErrorMessage('password_confirmation') }}
                </span>
            </div>

            <div class="update-password__action">
                <ButtonLoading :loading="loading" type="submit">
                    {{ $t('profile.update_password.btn') }}
                </ButtonLoading>
            </div>
        </div>
    </form>
</template>

<style scoped lang="scss">
.update-password {
    &__title {
        color: #454261;
        text-align: center;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 25px;
        text-transform: uppercase;
        margin-bottom: 10px;
    }

    &__fields {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-bottom: 14px;
    }

    &__item {
        display: flex;
        flex-direction: column;
        gap: 4px;

        &-label {
            color: #404040;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 21px;
            text-transform: uppercase;
        }

        &-input {
            position: relative;
        }

        &-error {
            display: block;
            min-height: 16px;
            color: var(--danger-color);
            font-size: 12px;
            line-height: 16px;
        }

        .input-password-icon {
            position: absolute;
            top: 46%;
            transform: translate(0, -50%);
            right: 10px;
            color: #656565;
            display: flex;
        }
    }

    &__action {
        display: flex;
        align-items: center;
        justify-content: center;
    }
}
</style>
